import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { Title, SidepanelTitle as UiSidepanelTitle } from '~/ui/domain/Sidepanel/components/SidepanelTitle';

import { useTitleThreadState } from '../job-profile-section/comments/CommentableTitle';
import { useTitleCommentRow } from '../job-profile-section/comments/title-comment-row-context';
import { JobProfileStatusStamps } from '../job-profile-section/JobProfileStatusStamps';

export { Title };

type SidepanelTitleProps = {
    readonly architectureId?: string;
    readonly hasComment?: boolean;
    readonly isCommentActive?: boolean;
    readonly isEditing: boolean;
    readonly itemId?: string;
    readonly itemType?: 'job-level' | 'job-role';
    readonly onInput?: () => void;
    readonly register: UseFormRegisterReturn<`translations.${number}.title`>;
    readonly title: string;
    readonly version?: string;
};

export const SidepanelTitle: FunctionComponent<SidepanelTitleProps> = ({
    architectureId,
    hasComment,
    isCommentActive,
    isEditing,
    itemId,
    itemType,
    onInput,
    register,
    title,
    version,
}) => {
    const { applicationState } = useApplicationContext();
    const isReviewStatusTableEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('review-status-table');
    const titleThreadState = useTitleThreadState();
    const { rowDataAttrs, onRowClick, titleInputRef } = useTitleCommentRow();
    const effectiveIsCommentActive = isCommentActive ?? titleThreadState.isActive;
    const effectiveHasComment = hasComment ?? titleThreadState.hasComment;

    if (!isReviewStatusTableEnabled) {
        return (
            <UiSidepanelTitle
                commentHighlightClassName={titleThreadState.highlightClassName}
                hasComment={effectiveHasComment}
                inputRef={titleInputRef}
                isCommentActive={effectiveIsCommentActive}
                isEditing={isEditing}
                onInput={onInput}
                onRowClick={onRowClick}
                register={register}
                rowAttrs={rowDataAttrs}
                title={title}
            />
        );
    }

    const versionNumber = is.nonEmptyStringAndNotWhitespace(version) ? Number(version) : Number.NaN;
    const canRenderStamps =
        !is.nullOrUndefined(architectureId) &&
        !is.nullOrUndefined(itemId) &&
        !is.nullOrUndefined(itemType) &&
        Number.isFinite(versionNumber);

    const stamps = canRenderStamps ? (
        <JobProfileStatusStamps
            architectureId={architectureId}
            currentVersion={versionNumber}
            itemId={itemId}
            itemType={itemType}
        />
    ) : undefined;

    return (
        <UiSidepanelTitle
            className="mt-12xl"
            commentHighlightClassName={titleThreadState.highlightClassName}
            hasComment={effectiveHasComment}
            inputRef={titleInputRef}
            isCommentActive={effectiveIsCommentActive}
            isEditing={isEditing}
            onInput={onInput}
            onRowClick={onRowClick}
            register={register}
            rowAttrs={rowDataAttrs}
            stamps={stamps}
            title={title}
        />
    );
};
